"use client";

import { Accordion as AccordionPrimitive } from "@base-ui/react/accordion";
import slugify from "@sindresorhus/slugify";
import { Fragment } from "react";

import { JsonLdScript } from "@/components/structured-data/json-ld-script";
import { Section } from "@/components/ui/section";
import { cn } from "@/lib/utils";

interface FaqQuestion {
  question: string;
  answer: string;
}

interface FaqCardProps {
  title: string;
  questions: FaqQuestion[];
  /** Index of the question open by default. */
  defaultOpenIndex?: number;
  className?: string;
}

function ToggleIcon() {
  return (
    <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-white shadow-[inset_0_0_0_1px_#e5e6e6]">
      <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        aria-hidden="true"
        className="hidden group-data-open:block"
      >
        <path
          d="M1.5 6 L10.5 6"
          stroke="#0c0d0d"
          strokeWidth="1.2"
          strokeLinecap="round"
        />
      </svg>
      <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        aria-hidden="true"
        className="group-data-open:hidden"
      >
        <path
          d="M6 1.5 L6 10.5 M1.5 6 L10.5 6"
          stroke="#0c0d0d"
          strokeWidth="1.2"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
}

export function FaqCard({
  title,
  questions,
  defaultOpenIndex = 1,
  className,
}: FaqCardProps) {
  const faqPageSchema =
    questions.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: questions.map((q) => ({
            "@type": "Question",
            name: q.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: q.answer,
            },
          })),
        }
      : null;

  const defaultValue = questions[defaultOpenIndex]?.question;

  return (
    <>
      {faqPageSchema && <JsonLdScript json={faqPageSchema} />}
      <Section className={cn(className, "bg-[#fafaf7] lg:px-20")}>
        <div className="relative mx-auto flex max-w-[1280px] flex-col gap-8 rounded-2xl border border-[#e6e6e6] bg-white p-6 lg:min-h-[644px] lg:flex-row lg:gap-[10px] lg:rounded-none lg:p-16">
          <h2 className="w-full shrink-0 text-[24px] leading-[1.2] font-normal tracking-[-0.04em] text-black lg:w-[260px] lg:text-[32px]">
            {title}
          </h2>
          <AccordionPrimitive.Root
            defaultValue={defaultValue ? [defaultValue] : undefined}
            className="flex w-full flex-col gap-4 lg:ml-[112px] lg:w-[780px]"
          >
            {questions.map((question) => {
              const triggerId = slugify(`faq-${question.question}`);
              return (
                <Fragment key={question.question}>
                  <AccordionPrimitive.Item
                    value={question.question}
                    className="group flex flex-col gap-4 rounded-2xl border border-[#e5e6e6] bg-[#f9f9f8] p-6"
                  >
                    <AccordionPrimitive.Header>
                      <AccordionPrimitive.Trigger
                        id={triggerId}
                        className="flex w-full flex-row items-center justify-between gap-4 text-start"
                      >
                        <span className="text-[16px] leading-[1.5] tracking-[-0.02em] text-black">
                          {question.question}
                        </span>
                        <ToggleIcon />
                      </AccordionPrimitive.Trigger>
                    </AccordionPrimitive.Header>
                    <AccordionPrimitive.Panel
                      keepMounted
                      aria-labelledby={triggerId}
                      className="overflow-hidden data-closed:invisible data-closed:h-0"
                    >
                      <span className="text-[16px] leading-[1.5] tracking-[-0.02em] text-black">
                        {question.answer}
                      </span>
                    </AccordionPrimitive.Panel>
                  </AccordionPrimitive.Item>
                </Fragment>
              );
            })}
          </AccordionPrimitive.Root>
        </div>
      </Section>
    </>
  );
}
